import {
  rehydrateComplete,
  appUpdateLogoutRequired,
  appUpdateRefreshOptional,
  appUpdateRefreshRequired,
  requestLogout,
  screenResize,
} from "actions";
import { appUpdateStatus } from "constants/system";
import "lib/logRocket/initialize";
import "lib/sentry/initialize";
import "lib/userflow/initialize";

import { createRoot } from "react-dom/client";
import "react-table-hoc-fixed-columns/lib/styles.css";
import "react-table/react-table.css";
import "react-toastify/dist/ReactToastify.css";

import "typeface-roboto";
import "./components/PageShell/animations.css";
import { GOOGLE_MAPS_API_KEY } from "./lib/googleMaps";
import {
  setGoogleMapsApiKey,
  setGoogleMapsApiLibraries,
} from "./lib/googleMaps/serviceManager";
import toast, {
  UPDATE_TOAST_MESSAGE,
  UPDATE_TOAST_MESSAGE_LOGOUT,
} from "./lib/toast";
import registerServiceWorker from "./registerServiceWorker";
import { debugServiceWorkerLog } from "lib/errors";
import sagas from "./sagas";

import DateFnsUtils from "@date-io/date-fns";
import { MuiPickersUtilsProvider } from "@material-ui/pickers";
import { ThemeProvider } from "@material-ui/styles";

import { muiTheme } from "constants/muiTheme";
import theme from "constants/theme";

import { GlobalStyles } from "globalStyles";
import React from "react";

import { Router } from "react-router-dom";

import { ThemeProvider as StyledComponentsThemeProvider } from "styled-components";
import history from "appHistory";
import Root from "containers/Root";

import { getStore, createStore } from "store";

import { addModalManager } from "./lib/modalManager";

setGoogleMapsApiKey(GOOGLE_MAPS_API_KEY);
setGoogleMapsApiLibraries(["places"]);

const handleUpdateFound = status => {
  const store = getStore();
  debugServiceWorkerLog(`Handle Update Found - status: ${status}`);
  switch (status) {
    case appUpdateStatus.LOGOUT_REQUIRED:
      store.dispatch(requestLogout());
      store.dispatch(appUpdateLogoutRequired());
      toast.refreshPrompt(UPDATE_TOAST_MESSAGE_LOGOUT);
      break;

    case appUpdateStatus.REFRESH_REQUIRED:
      store.dispatch(appUpdateRefreshRequired());
      toast.refreshPrompt(UPDATE_TOAST_MESSAGE);
      break;

    case appUpdateStatus.REFRESH_OPTIONAL:
      store.dispatch(appUpdateRefreshOptional());
      break;

    case appUpdateStatus.INVISIBLE:
      // Do nothing
      /* eslint-disable-next-line no-console */
      console.log("App updates are ready to install");
      break;

    default:
  }
};

const container = document.getElementById("root");
const root = createRoot(container);

function onRehydrated() {
  const store = getStore();

  sagas.forEach(saga => store.runSaga(saga));

  registerServiceWorker(handleUpdateFound);

  addModalManager();

  window.addEventListener("resize", () => {
    const state = store.getState();
    if (state && state.screen.width !== window.innerWidth) {
      store.dispatch(screenResize(window.innerWidth));
    }
  });

  store.dispatch(rehydrateComplete());

  root.render(
    <StyledComponentsThemeProvider theme={theme}>
      <MuiPickersUtilsProvider utils={DateFnsUtils}>
        <ThemeProvider theme={muiTheme}>
          <GlobalStyles />
          <Router history={history}>
            <Root store={getStore()} />
          </Router>
        </ThemeProvider>
      </MuiPickersUtilsProvider>
    </StyledComponentsThemeProvider>,
  );
}

createStore(onRehydrated);
